import React from "react";
import { Hero, PageContent } from "../components";

function PartCollection() {
  return (
    <div className="flex overflow-x-hidden overflow-y-hidden relative flex-col z-8">
      <Hero title={"Part Collection"}>
        We contributed to the registry of iGEM,{" "}
        <span className="text-lg font-black text-BleuClair md:text-xl lg:text-2xl">
          Discover how.
        </span>
      </Hero>

      <PageContent>
        {`

## Registry contribution
We have created several parts aiming at developping our project but also contributing to the Registry for future iGEM team.

<div class="image_frame">
    <img class="illustration big" src="https://static.igem.wiki/teams/4577/wiki/part-collection/tableau-parts-collection.png">
</div>

## Parts for Meli
To degrade 17α-ethynylestradiol (EE2), we selected the genes *amoa* (BBa_K4577005) and *hao* (BBa_K4577004) which respectively encode the enzymes AMO (Ammonia monooxygenase) and Hao (hydroxylamine oxidoreductase).

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/part-collection/plasmid-meli.jpg">
</div>

Several studies have reported that ammonia-oxidizing bacteria can notably degrade estrogens, including EE2. The AmoA and Hao enzymes naturally combine to convert ammonia into nitrite in ammonia-oxidizing bacteria. Their function is crucial in this type of bacteria. It is supposed that there is a close connection between the conversion of ammonia to nitrite and the elimination of cometabolism EE2.[1] 

In the chain reaction, the enzyme AMO initially transforms NH3 into NH2-OH. The NH2OH produced by AMO is later oxidized by the enzyme Hao into NO2-.[2] The final product of degradation is EE2-OH.

The *Ensifer meliloti* Sm1021 strain is employed in our project, and the gene's codons have been optimized for this bacterium. Both genes are integrated into the pUCIDT-KanR plasmid.

- The *amoa* gene is flanked by the strong tac promoter and the lambda t0 terminator on one side (BBa_K4577008). Additionally, an efficient ribosome binding site (RBS) has been added between the promoter and the gene.
- The *hao* gene is flanked by the strong T7 promoter (BBa_K4577006) and terminator, which are derived from the T7 bacteriophage, on the other side (BBa_K4577007).

Moreover, a ribosome binding site RBS (g10-L from the T7 bacteriophage) has been added between the promoter and the gene.

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/part-collection/hao-amoa-final-map.png">
    <p>Composite Part numbers: Hao gene with the promotor, terminator and the efficient RBS (g10-L) from bacteriophage T7(BBa_K4577007) and AmoA gene with the tac promotor, the strong bacterial RBS and the lambda t0 terminator (BBa_K4577008)</p>
</div>

## Parts for Chlamy
For heat production we chose the *sraox* gene coming from the skunk cabbage *Symplocarpus renifolius*. This gene encodes for a thermogenic protein which allows the plant to produce a heat differential of 35°C.  This protein located in the inner part of the mitochondria is able to oxidize the ubiquinol and transfer the electron to dioxygene which is reduced into water. During the reaction energy is dissipated into heat.[3] 

*Chlamydomonas reinhardtii* is an interesting chassis for us as it contains mitochondria and has water depolluting properties. However, genetically engineering of *C. reinhardtii* requires some gene adaptation to avoid gene silencing. It is with this point in mind we developed our parts.

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/part-collection/plasmid-chlamy.png">
</div>

First, we codon optimized our gene sequence to reach around 65% of CG contents. Then as *C. reinhardtii* has a very high level of introns in its genome (around 7 per gene) we added either 3 or 1 introns inside our coding sequence to obtain respectively BBa_K4577000 and BBa_K4577001 parts. The introns sequences are coming from the rbcs2 gene. For BBa_K4577000, one intron has been located in 5’, the second one around 500 base pair later and finally the last one after the stop codon. For BBa_K4577001, only an intron has been inserted in middle of the coding sequence.[4] 

The second step of our gene engineering was to add a signal peptide to target the AOX protein to the mitochondria. This peptide signal has been placed in 5’ flank of the first exon. 

Finally, as we wanted to facilitate protein characterization and being able to check the colocalization of our protein we also added a His tag.  In literature, a tag already has been place in N-terminal of the protein to reproduce this scheme and placed it right before the signal peptide. To reduce the impact of our addition to the coding sequence linker have been placed between the tag and the peptide signal and between the peptide signal and the first exon.

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/part-collection/aox-1intron-final-map.png">
    <p>Composite Part number: srAOX with an intron, pPSAD strong promoter and tPSAD terminator (BBa_K4577002)</p>
</div>

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/part-collection/aox-3introns-final-map.png">
    <p>Composite Part number: srAOX with three introns, pPSAD strong promoter and tPSAD terminator (BBa_K4577009)</p>
</div>

To enhance gene expression, we selected a strong promoter for *C. reinhardtii* (BBa_K4316025) and its associated terminator (BBa_K4316024). Our composite parts BBa_K4577002 and BBa_K4577009 were inserted into the pMBS810 vector coming from MoClo Kit and containing a hygromycin resistance gene.


# Sources:

[1] Sheng Q, Yi M, Men Y, et al. Cometabolism of 17α-ethynylestradiol by nitrifying bacteria depends on reducing power availability and leads to elevated nitric oxide formation[J]. Environment International, 2021, 153(11):106528. 

[2] Fan Ouyanga, Dan Penga,Xiao Su, Population dynamics and metabolic alternations of AOB in activated sludge under Cu shock loads, Desalination and Water Treatment, october 2020, doi: 10.5004/dwt.2020.26102 

[3]  Zhu, Yan et al. 2011. « Regulation of Thermogenesis in Plants: The Interaction of Alternative Oxidase and Plant Uncoupling Mitochondrial Protein ». Journal of Integrative Plant Biology 53(1): 7‑13. 

[4] Schroda, M. (2019). Good News for Nuclear Transgene Expression in Chlamydomonas. Cells, 8(12), 1534. https://doi.org/10.3390/cells8121534 
          `}
      </PageContent>
    </div>
  );
}

export default PartCollection;
